import { useEffect, useRef, useState } from 'react';
import './RgbComparison.css';

export function thermalViewport(rect, zoom, offset) {
  if (!rect.width || !rect.height) return { zoom: 1, centerX: .5, centerY: .5 };
  return {
    zoom,
    centerX: (((rect.left * 2 + rect.width) / 2 - offset.x) / zoom - rect.left) / rect.width,
    centerY: (((rect.top * 2 + rect.height) / 2 - offset.y) / zoom - rect.top) / rect.height,
  };
}

export function linkedRgbTransform(rect, viewport) {
  const x = (rect.left * 2 + rect.width) / 2 - (rect.left + viewport.centerX * rect.width) * viewport.zoom;
  const y = (rect.top * 2 + rect.height) / 2 - (rect.top + viewport.centerY * rect.height) * viewport.zoom;
  return `translate(${x}px, ${y}px) scale(${viewport.zoom})`;
}

export default function RgbComparison({ images, selectedId, viewport = { zoom: 1, centerX: .5, centerY: .5 } }) {
  const selected = images.find(image => image.id === selectedId);
  return <section className="rgb-comparison" aria-label="RGB comparison">
    <div className="rgb-comparison-header">
      <strong>RGB</strong>
      {selected && <span className="rgb-comparison-name">{selected.name}</span>}
      <small>Linked zoom and pan · {Math.round(viewport.zoom * 100)}%</small>
    </div>
    {selected ? <RgbImage key={selected.id} image={selected} viewport={viewport} /> : <p className="rgb-comparison-empty">No matching RGB image found. Upload the image with the same filename ending in _V.</p>}
  </section>;
}

function RgbImage({ image, viewport }) {
  const [failed, setFailed] = useState(false);
  const stageRef = useRef(null), imageRef = useRef(null);
  const [rect, setRect] = useState({ left: 0, top: 0, width: 0, height: 0 });
  useEffect(() => {
    const stage = stageRef.current, img = imageRef.current;
    if (!stage || !img) return;
    const measure = () => {
      if (!img.naturalWidth || !img.naturalHeight) return;
      const scale = Math.min(stage.clientWidth / img.naturalWidth, stage.clientHeight / img.naturalHeight);
      const width = img.naturalWidth * scale, height = img.naturalHeight * scale;
      setRect({ left: (stage.clientWidth - width) / 2, top: (stage.clientHeight - height) / 2, width, height });
    };
    measure(); img.addEventListener('load', measure);
    const observer = new ResizeObserver(measure); observer.observe(stage);
    return () => { observer.disconnect(); img.removeEventListener('load', measure); };
  }, [image.url]);
  return <div className="rgb-comparison-stage" ref={stageRef}>
    {failed ? <p role="alert">Could not load the RGB image. Close and reopen comparison to retry.</p> :
      <div className="rgb-comparison-transform" style={{ transform: linkedRgbTransform(rect, viewport) }}>
        <img ref={imageRef} src={image.url} alt={`RGB comparison: ${image.name}`} onError={() => setFailed(true)} draggable={false} />
      </div>}
  </div>;
}
